import { createId } from "../../app/runtimeContext";
import type { CyanIntelligenceRepository } from "./repositories";
import type { CyanGoalScope, CyanStatus, Goal, Milestone } from "./types";

export type CreateGoalInput = {
  organizationId: string;
  productId?: string;
  departmentId?: string;
  owner: string;
  scope: CyanGoalScope;
  title: string;
  description: string;
  milestones: Array<Omit<Milestone, "id" | "status">>;
};

export class GoalManager {
  constructor(private readonly repository: CyanIntelligenceRepository) {}

  async createGoal(input: CreateGoalInput): Promise<Goal> {
    const now = new Date().toISOString();
    const goal: Goal = {
      ...input,
      id: createId("goal"),
      status: "planned",
      milestones: input.milestones.map((milestone) => ({
        ...milestone,
        id: createId("milestone"),
        status: "planned",
      })),
      completionPercent: 0,
      createdAt: now,
      updatedAt: now,
    };
    await this.repository.saveGoal(goal);
    return goal;
  }

  calculateProgress(milestones: Milestone[]): number {
    const totalWeight = milestones.reduce(
      (sum, milestone) => sum + milestone.weight,
      0,
    );
    if (totalWeight === 0) return 0;
    const completedWeight = milestones
      .filter((milestone) => milestone.status === "completed")
      .reduce((sum, milestone) => sum + milestone.weight, 0);
    return Math.round((completedWeight / totalWeight) * 100);
  }

  async updateMilestone(
    goal: Goal,
    milestoneId: string,
    status: CyanStatus,
  ): Promise<Goal> {
    const updated = {
      ...goal,
      milestones: goal.milestones.map((milestone) =>
        milestone.id === milestoneId ? { ...milestone, status } : milestone,
      ),
      updatedAt: new Date().toISOString(),
    };
    updated.completionPercent = this.calculateProgress(updated.milestones);
    updated.status =
      updated.completionPercent === 100 ? "completed" : updated.status;
    await this.repository.saveGoal(updated);
    return updated;
  }
}
